import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { adminLogin } from "../services/authService";
import {
  loginSuccess,
  logout,
  setLoading,
  setError,
  clearError,
  initializeAuth,
} from "../store/slices/authSlice";
import { useAuthRedux } from "../store/hooks";

// Initialize auth on app load
export const useAuthInitializer = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);
};

// Custom hook that combines Redux state with React Query mutation
export const useAuth = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const authRedux = useAuthRedux();

  // Login mutation with React Query
  const loginMutation = useMutation({
    mutationFn: async (credentials) => {
      console.log("Mutation function called with:", {
        email: credentials.email,
        password: "***",
      });
      dispatch(setLoading(true));
      dispatch(clearError());
      
      try {
        const result = await adminLogin(credentials);
        console.log("Mutation function result:", result);
        return result;
      } catch (error) {
        console.error("Mutation function error:", error);
        throw error;
      }
    },
    retry: false, // Don't retry on error
    onSuccess: (data) => {
      console.log("Mutation onSuccess called with:", data);
      
      // Extract token and remove "Bearer " prefix if present
      let token = data.token || data.data?.token || data.accessToken || "";
      if (token.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      
      // Extract user/admin data
      const user = data.admin || data.user || data.data?.admin || data.data?.user || data;

      // Update Redux store
      dispatch(loginSuccess({ token, user }));
    },
    onError: (error) => {
      console.error("Mutation onError called with:", error);
      console.error("Error details:", {
        message: error?.message,
        response: error?.response,
        config: error?.config,
      });
      
      // Update Redux store with error
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Login failed. Please check your credentials and try again.";
      dispatch(setError(errorMessage));
    },
  });

  // Logout function - resets everything
  const handleLogout = () => {
    // Clear Redux state
    dispatch(logout());
    
    // Clear React Query cache
    queryClient.clear();
    
    // Reset login mutation
    loginMutation.reset();
  };

  // Reset login mutation
  const resetLogin = () => {
    loginMutation.reset();
    dispatch(clearError());
  };

  return {
    // Redux state
    isAuthenticated: authRedux.isAuthenticated,
    user: authRedux.user,
    token: authRedux.token,
    isLoading: authRedux.isLoading || loginMutation.isPending,
    error: authRedux.error || loginMutation.error,
    isError: !!authRedux.error || loginMutation.isError,
    
    // Actions
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    resetLogin,
    logout: handleLogout,
    updateUser: authRedux.updateUser,
    clearError: () => {
      dispatch(clearError());
      loginMutation.reset();
    },
  };
};

